import {
  Acknowledgement,
  AcknowledgementName,
  AcknowledgementStatus,
  Payer,
  PayerStatus,
  PayerTaxInformationShareStatus
} from "@wingspanhq/wingspan-api-sdk";
import { queriesAcknowledgements, queriesPayers } from "@query";
import { WSQueryResult } from "@ws-react-query";
import { CertifyTaxPayerInfo } from "../steps/CertifyTaxPayerInfo";
import { Tax1099Consent } from "../steps/CertifyTaxPayerInfo/Tax1099Consent";
import {
  OnboardingContext,
  OnboardingModuleCertifyTaxPayerInfo
} from "../types";
import {
  buildOnboardingStep,
  UseCreateOnboardingStep,
  useOnboardingStepQueryConfig
} from "../components/OnboardingFlowUtils";
import { FlowStep } from "../../../shared/components/FlowSwitch/types";

const getStatus = (
  payers?: Payer[],
  electronicTaxFormConsent?: Acknowledgement
) => {
  const activePayers = payers?.filter(
    payer => payer.status === PayerStatus.ACTIVATED
  );

  const isTaxInfoSharedToPayers = activePayers?.every(
    payer =>
      payer.taxInformationShare?.status ===
      PayerTaxInformationShareStatus.ACTIVE
  );

  // Declining e-delivery is a valid answer, but v3 reports a never-answered
  // acknowledgement as NotGiven with no version — only a recorded answer has one.
  const isElectronicTaxFormConsentAnswered =
    electronicTaxFormConsent?.acknowledgementStatus ===
      AcknowledgementStatus.GIVEN || !!electronicTaxFormConsent?.version;

  if (isTaxInfoSharedToPayers && isElectronicTaxFormConsentAnswered) {
    return "Complete";
  }

  return "None";
};

export const getCertifyTaxPayerInfoStepConfig = (
  _: OnboardingModuleCertifyTaxPayerInfo,
  __: OnboardingContext,
  payers: Payer[] = [],
  electronicTaxFormConsent?: Acknowledgement
): FlowStep => {
  const slug = "taxpayer-info";

  const steps: FlowStep[] = [
    {
      title: "Certify taxpayer info",
      slug: "certify",
      component: CertifyTaxPayerInfo
    },
    {
      title: "Consent to e-delivery",
      slug: "e-delivery",
      component: Tax1099Consent
    }
  ];

  const status = getStatus(payers, electronicTaxFormConsent);

  return {
    title: "Certify taxpayer info",
    slug,
    steps,

    status,
    result: {
      Complete: {
        title: "You certified your taxpayer information",
        description:
          "You certified your taxpayer information is accurate. Click continue for the next step."
      }
    }
  };
};

export const useCertifyTaxPayerInfoStep: UseCreateOnboardingStep<
  OnboardingModuleCertifyTaxPayerInfo
> = modules => {
  const config = useOnboardingStepQueryConfig(
    modules,
    "certify_tax_payer_info"
  );
  const queryAllPayerList = queriesPayers.listPayers.useQueryAll(
    undefined,
    config
  );
  const queryElectronicTaxFormConsentAcknowledgement =
    queriesAcknowledgements.getAcknowledgement.useQuery(
      {
        acknowledgementName: AcknowledgementName.ELECTRONIC_TAX_FORM_CONSENT
      },
      config
    );

  return (onboardingModule, contextValue) =>
    buildOnboardingStep({
      getConfig: getCertifyTaxPayerInfoStepConfig,
      module: onboardingModule,
      context: contextValue,
      queries: [
        // buildOnboardingStep feeds each query's `.data` positionally into
        // getConfig; useQueryAll's `.data` is InfiniteData, so expose the flat
        // Payer[] as `.data`. Cast at this framework boundary (the step-builder
        // types elements as WSQueryResult, not the infinite variant).
        {
          ...queryAllPayerList,
          data: queryAllPayerList.flatData ?? []
        } as unknown as WSQueryResult<Payer[]>,
        queryElectronicTaxFormConsentAcknowledgement
      ]
    });
};
